import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
    FiPlus, FiEdit2, FiTrash2, FiClipboard,
    FiBarChart2, FiAlertTriangle, FiBookOpen,
} from "react-icons/fi";
import { useApp } from "../../context/AppContext";
import examService from "../../services/examService";
import courseService from "../../services/courseService";
import { getYearOptions, getSemesterOptions } from "../../utils/courseYears";
import ConfirmModal from "../../components/common/ConfirmModal";

const TYPE_OPTIONS = ["Internal", "External", "Practical", "Sessional", "University", "Other"];

const STATUS_STYLES = {
    Draft: { bg: "#f3f4f6", color: "#6b7280" },
    Active: { bg: "#e8f1f8", color: "#002747" },
    "Results Published": { bg: "#dcfce7", color: "#15803d" },
};

const TYPE_COLORS = {
    Internal: "#003b67",
    External: "#7c3aed",
    Practical: "#0891b2",
    Sessional: "#d97706",
    University: "#dc2626",
    Other: "#6b7280",
};

const normalizeExamList = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
};

export default function ExamList() {
    const navigate = useNavigate();
    const { academicYear } = useApp();

    const [exams, setExams] = useState([]);
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleting, setDeleting] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const [courseFilter, setCourseFilter] = useState("");
    const [yearFilter, setYearFilter] = useState("");
    const [semesterFilter, setSemesterFilter] = useState("");
    const [typeFilter, setTypeFilter] = useState("");

    // ── Fetch courses from courseService ─────────────────────────────────────
    useEffect(() => {
        const fetchCourses = async () => {
            try {
                const res = await courseService.getAll({ isActive: true });
                const courseList = Array.isArray(res.data) ? res.data : (Array.isArray(res) ? res : []);
                setCourses(courseList);
            } catch (err) {
                console.error("Failed to fetch courses:", err);
            }
        };
        fetchCourses();
    }, []);

    // Derived year and semester options based on selected course
    const selectedCourse = courses.find((c) => (c.shortName || c.courseName) === courseFilter);
    const yearOptions = selectedCourse ? getYearOptions(selectedCourse) : [];
    const selectedYearNumber = yearOptions.find((y) => y.label === yearFilter)?.yearNumber;
    const semesterOptions = selectedCourse
        ? getSemesterOptions(selectedCourse, selectedYearNumber)
        : [];

    const handleCourseChange = (e) => {
        setCourseFilter(e.target.value);
        setYearFilter(""); // Reset year when course changes
        setSemesterFilter(""); // Reset semester
    };

    const fetchExams = useCallback(async () => {
        if (!academicYear?._id) { setExams([]); setLoading(false); return; }
        setLoading(true);
        try {
            const params = {
                academicYear: academicYear._id,
                courseAdmitted: courseFilter,
                courseYear: yearFilter,
                examType: typeFilter,
            };
            if (semesterFilter) {
                params.semesterNumber = Number(semesterFilter);
            }
            const res = await examService.getAll(params);
            setExams(normalizeExamList(res));
        } catch (err) {
            setExams([]);
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    }, [academicYear, courseFilter, yearFilter, semesterFilter, typeFilter]);

    useEffect(() => { fetchExams(); }, [fetchExams]);

    const handleDelete = async () => {
        setDeleting(true);
        try {
            const res = await examService.remove(deleteTarget._id);
            toast.success(res.message);
            setDeleteTarget(null);
            fetchExams();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setDeleting(false);
        }
    };

    const clearFilters = () => {
        setCourseFilter("");
        setYearFilter("");
        setSemesterFilter("");
        setTypeFilter("");
    };
    const hasFilters = Boolean(courseFilter || yearFilter || semesterFilter || typeFilter);

    return (
        <div>
            {/* ── Header ── */}
            <div className="page-header">
                <div>
                    <h1 className="page-title">Exams & Results</h1>
                    <p className="page-subtitle">
                        {academicYear
                            ? `${exams.length} exam(s) — ${academicYear.label}`
                            : "Select an academic year"}
                    </p>
                </div>
                <Link to="/exams/new" className="btn btn-primary">
                    <FiPlus size={14} /> Create Exam
                </Link>
            </div>

            {/* ── No Year Warning ── */}
            {!academicYear && (
                <div style={{ background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 8, padding: "0.75rem 1rem", marginBottom: "1rem", fontSize: 13, color: "#92400e", display: "flex", alignItems: "center", gap: 8 }}>
                    <FiAlertTriangle size={14} /> Select an academic year from the Dashboard to view exams.
                </div>
            )}

            <div className="card">
                {/* ── Filters ── */}
                <div style={{ padding: "0.75rem 1rem", borderBottom: "1px solid var(--border)", display: "flex", gap: "0.75rem", flexWrap: "wrap", alignItems: "center" }}>
                    <select className="form-control" style={{ width: 160, fontSize: 13 }} value={courseFilter} onChange={handleCourseChange}>
                        <option value="">All Courses</option>
                        {courses.map((c) => (
                            <option key={c._id || c.shortName} value={c.shortName}>
                                {c.shortName}
                            </option>
                        ))}
                    </select>

                    <select
                        className="form-control"
                        style={{ width: 150, fontSize: 13 }}
                        value={yearFilter}
                        onChange={(e) => {
                            setYearFilter(e.target.value);
                            setSemesterFilter("");
                        }}
                        disabled={!courseFilter}
                    >
                        <option value="">{courseFilter ? "All Years" : "Select Course First"}</option>
                        {yearOptions.map((y) => (
                            <option key={y.yearNumber} value={y.label}>
                                {y.label}
                            </option>
                        ))}
                    </select>

                    <select
                        className="form-control"
                        style={{ width: 150, fontSize: 13 }}
                        value={semesterFilter}
                        onChange={(e) => setSemesterFilter(e.target.value)}
                        disabled={!courseFilter || semesterOptions.length === 0}
                    >
                        <option value="">All Semesters</option>
                        {semesterOptions.map((s) => (
                            <option key={s.semesterNumber} value={s.semesterNumber}>
                                {s.label}
                            </option>
                        ))}
                    </select>

                    <select className="form-control" style={{ width: 160, fontSize: 13 }} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                        <option value="">All Types</option>
                        {TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>

                    {hasFilters && (
                        <button type="button" className="btn btn-outline btn-sm" onClick={clearFilters}>
                            Clear Filters
                        </button>
                    )}
                </div>

                {/* ── Table ── */}
                {loading ? (
                    <div className="loading-center"><div className="spinner" />Loading exams...</div>
                ) : exams.length === 0 ? (
                    <div className="empty-state">
                        <FiBookOpen size={48} style={{ opacity: 0.3, margin: "0 auto 0.75rem" }} />
                        <p>{academicYear ? "No exams found. Create one to get started." : "No academic year selected."}</p>
                    </div>
                ) : (
                    <div className="table-wrapper">
                        <table>
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Exam Title</th>
                                    <th>Type</th>
                                    <th>Course & Sem</th>
                                    <th>Date</th>
                                    <th>Subjects</th>
                                    <th>Results</th>
                                    <th>Status</th>
                                    <th style={{ textAlign: "right" }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {exams.map((exam, i) => {
                                    const ss = STATUS_STYLES[exam.status] || STATUS_STYLES.Draft;
                                    const tc = TYPE_COLORS[exam.examType] || "#6b7280";
                                    const subjectCount = Array.isArray(exam.subjects) ? exam.subjects.length : 0;
                                    return (
                                        <tr key={exam._id}>
                                            <td style={{ color: "var(--text-muted)", fontSize: 12 }}>{i + 1}</td>
                                            <td>
                                                <div style={{ fontWeight: 600 }}>{exam.title}</div>
                                                {exam.notes && (
                                                    <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>{exam.notes}</div>
                                                )}
                                            </td>
                                            <td>
                                                <span style={{ background: `${tc}18`, color: tc, padding: "2px 8px", borderRadius: 99, fontSize: 11.5, fontWeight: 600 }}>
                                                    {exam.examType}
                                                </span>
                                            </td>
                                            <td style={{ fontSize: 13 }}>
                                                <div style={{ fontWeight: 600 }}>{exam.courseAdmitted}</div>
                                                <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                                                    {exam.courseYear}
                                                    {exam.semesterNumber ? ` · Sem ${exam.semesterNumber}` : (exam.semester ? ` · ${exam.semester}` : "")}
                                                </div>
                                            </td>
                                            <td style={{ fontSize: 13 }}>{exam.examDate || "—"}</td>
                                            <td style={{ fontSize: 13 }}>{subjectCount} subject{subjectCount !== 1 ? "s" : ""}</td>
                                            <td style={{ fontSize: 13 }}>
                                                {exam.resultCount > 0 ? (
                                                    <span>
                                                        {exam.resultCount} entered ·{" "}
                                                        <span style={{ color: "#16a34a", fontWeight: 600 }}>{exam.passCount} passed</span>
                                                    </span>
                                                ) : (
                                                    <span style={{ color: "var(--text-muted)" }}>None yet</span>
                                                )}
                                            </td>
                                            <td>
                                                <span style={{ background: ss.bg, color: ss.color, padding: "2px 8px", borderRadius: 99, fontSize: 11.5, fontWeight: 500 }}>
                                                    {exam.status}
                                                </span>
                                            </td>
                                            <td>
                                                <div style={{ display: "flex", gap: "0.35rem", justifyContent: "flex-end" }}>
                                                    <button className="btn btn-outline btn-sm" title="Enter Results" onClick={() => navigate(`/exams/${exam._id}/results/entry`)}>
                                                        <FiClipboard size={12} /> Entry
                                                    </button>
                                                    <button className="btn btn-outline btn-sm" title="View Results" onClick={() => navigate(`/exams/${exam._id}/results`)}>
                                                        <FiBarChart2 size={12} /> Results
                                                    </button>
                                                    <button className="btn btn-outline btn-sm" title="Edit Exam" onClick={() => navigate(`/exams/${exam._id}/edit`)}>
                                                        <FiEdit2 size={12} />
                                                    </button>
                                                    <button className="btn btn-danger btn-sm" title="Delete" onClick={() => setDeleteTarget(exam)}>
                                                        <FiTrash2 size={12} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {deleteTarget && (
                <ConfirmModal
                    title="Delete Exam"
                    message={`Delete "${deleteTarget.title}"? All results for this exam will also be deleted permanently.`}
                    onConfirm={handleDelete}
                    onCancel={() => setDeleteTarget(null)}
                    loading={deleting}
                />
            )}
        </div>
    );
}